import { Injectable, OnDestroy } from '@angular/core';
import { distinctUntilChanged, filter, map, pluck, shareReplay, tap } from 'rxjs/operators';
import { filterNotNullish, filterNotUndefined } from '@app/core/utils/rxjs';
import { Store, select } from '@ngrx/store';
import * as InputsStore from '@app/shared/store/inputs';
import * as OrderStore from '@app/ecsomag/parcel/store/order';
import { UserService } from '@app/ecsomag/user/services/user.service';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { Router, NavigationEnd, EventType } from '@angular/router';
import { combineLatest, Subscription } from 'rxjs';
import { omit } from 'lodash-es';
import { ParcelSummaryTableRow } from '@app/models/prices.utils';

export interface AnalyticsEventLanguageData {
	language?: string;
}

export interface AnalyticsEventUserData {
	visitorType: 'unsigned' | 'signed' | 'signedInternal';
	userId?: number;
	userEmail?: string;
	visitorDeliveryHistoryOnline?: boolean;
	user_category: 'logged-out' | 'private' | 'business';
}

export type AnalyticsEventEcommerceCheckoutDataActionField =
	| {
			step: 1;
			// receiver_postcode?: string;
	  }
	| {
			step: 2;
			sender_postcode?: string;
			collection_date?: string;
	  }
	| {
			step: 3;
			user_type?: 'private' | 'business';
	  }
	| {
			step: 4;
			option: 'Összefoglalás';
	  };

export type AnalyticsEventEcommerceDataProduct = {
	type: 'parcel' | 'extraService' | 'discount' | 'other';
	id?: string;
	name: string;
	// 'id': '326465',
	price?: number;
	brand: 'GLS';
	category?: string;
	category2?: string;
	category3?: string;
	category4?: string;
	variant?: string;
	quantity: number;
};

export type AnalyticsEventPageviewType = 'virtualPageView';
export type AnalyticsEventEcommerceType = 'calculation' | 'checkout' | 'transaction' | 'transaction_error';
export type AnalyticsEventUserType = 'sign_up' | 'login' | 'logout' | 'loginFailed' | 'signupFailed';
interface AnalyticsEventData {
	event: AnalyticsEventPageviewType | AnalyticsEventEcommerceType | AnalyticsEventUserType;
}

export interface AnalyticsPageViewEventData extends AnalyticsEventData {
	event: 'virtualPageView';
	virtualPageURL: string;
}

export interface AnalyticsEventEcommerceCalculatorData extends AnalyticsEventData {
	event: 'calculation';
	ecommerce: {
		checkout: {
			products: AnalyticsEventEcommerceDataProduct[];
		};
	};
}

export interface AnalyticsEventEcommerceCheckoutData extends AnalyticsEventData {
	event: 'checkout';
	userData?: {
		email?: string;
		phone_number?: string;
	};
	ecommerce: {
		checkout: {
			actionField: AnalyticsEventEcommerceCheckoutDataActionField;
			products: AnalyticsEventEcommerceDataProduct[];
			cart_id: string;
		};
	};
}

export interface AnalyticsEventEcommerceTransactionData extends AnalyticsEventData {
	event: 'transaction';
	userData: {
		email?: string;
		phone_number?: string;
	};
	ecommerce: {
		currencyCode: 'HUF';
		purchase: {
			actionField: {
				id: string;
				transactionId: string;
				affiliation: 'ecsomag';
				revenue: number;
				// tax: number;
				// coupon: string;
			};
			products: AnalyticsEventEcommerceDataProduct[];
			cart_id: string;
		};
	};
}

export interface AnalyticsEventEcommerceTransactionErrorData extends AnalyticsEventData {
	event: 'transaction_error';
	userData: {
		email?: string;
		phone_number?: string;
	};
	ecommerce: {
		currencyCode: 'HUF';
		checkout: {
			actionField: {
				error_message: string;
				error_id?: string;
			};
			products: AnalyticsEventEcommerceDataProduct[];
		};
	};
}

export interface AnalyticsUserEventData extends AnalyticsEventData {
	errors?: string[];
}

export type AnalyticsEventEcommerceData = AnalyticsEventEcommerceCheckoutData | AnalyticsEventEcommerceTransactionData | AnalyticsUserEventData;

@Injectable({
	providedIn: 'root',
})
export class AnalyticsService implements OnDestroy {
	private languageData: AnalyticsEventLanguageData = {};

	private userData: AnalyticsEventUserData = {
		visitorType: 'unsigned',
		user_category: 'logged-out',
	};

	private readonly navigationEvent$ = this._router.events.pipe(
		filter(event => event instanceof NavigationEnd && event.type === EventType.NavigationEnd),
		tap(event => {
			console.log('navigationEvent$ = ', event);
		})
	);

	private readonly userData$ = this.user.user$.pipe(
		tap(user => {
			console.log('user$ = ', user);
		}),
		filterNotUndefined(),
		map(
			(user): AnalyticsEventUserData =>
				user
					? {
							visitorType: 'signed',
							userId: user.id,
							userEmail: user.email,
							visitorDeliveryHistoryOnline: user.ordersCount > 0,
							user_category: 'private',
					  }
					: {
							visitorType: 'unsigned',
							user_category: 'logged-out',
					  }
		),
		tap(userData => {
			this.userData = userData;
			console.log('userData$ = ', userData);
		}),
		shareReplay(1)
	);

	private readonly languageData$ = this._uiState.pipe(
		select(InputsStore.selectors.selectLanguage),
		filterNotNullish(),
		distinctUntilChanged(),
		map((language): AnalyticsEventLanguageData => ({ language })),
		tap(languageData => {
			this.languageData = languageData;
		}),
		shareReplay(1)
	);

	private _subscriptions: Subscription[] = [];

	constructor(private _router: Router, private user: UserService, private _uiState: Store<InputsStore.State>, private gtmService: GoogleTagManagerService) {
		let prevNavigationEvent: NavigationEnd | null = null;
		this._subscriptions.push(
			combineLatest([this.navigationEvent$, this.userData$, this.languageData$]).subscribe(([navigationEvent, userData, languageData]) => {
				if (navigationEvent instanceof NavigationEnd) {
					// skip emit if the navigationEvent is the same
					if (navigationEvent === prevNavigationEvent) return;
					prevNavigationEvent = navigationEvent;

					const gtmTag = {
						event: 'virtualPageView',
						virtualPageURL: navigationEvent.url,
						...userData,
						...languageData,
					};
					console.log('push gtm data', gtmTag);
					this.gtmService.pushTag(gtmTag).catch(() => void 0);
				}
			})
		);
	}

	ngOnDestroy(): void {
		this._subscriptions.forEach(subscription => subscription.unsubscribe());
	}

	public pushEcommerceEventCalculation(): void {
		try {
			const orderData = sessionStorage.getItem('orderData');
			if (typeof orderData !== 'string') return;
			const { order, products } = JSON.parse(orderData) as MappedAnalyticsData;

			const gtmTag: AnalyticsEventEcommerceCalculatorData = {
				event: 'calculation',
				ecommerce: {
					checkout: {
						products,
					},
				},
				...this.userData,
				...this.languageData,
			};

			console.log('push gtm data', gtmTag);

			this.gtmService.pushTag(gtmTag).catch(() => void 0);
			sessionStorage.removeItem('orderData');
		} catch (e) {
			console.warn(e);
		}
	}

	public pushEcommerceEventCheckout(step: 1 | 2 | 3 | 4): void {
		try {
			const orderData = sessionStorage.getItem('orderData');
			if (typeof orderData !== 'string') return;
			const { order, products } = JSON.parse(orderData) as MappedAnalyticsData;

			let actionField: AnalyticsEventEcommerceCheckoutDataActionField;
			switch (step) {
				case 1: {
					actionField = {
						step: 1,
					};
					break;
				}
				case 2: {
					actionField = {
						step: 2,
						sender_postcode: order.pickupAddress?.zipCode,
						collection_date: order.pickupDate ? order.pickupDate : undefined,
					};
					break;
				}
				case 3: {
					let userType: 'private' | 'business' | undefined = undefined;
					switch (order.billingAddress?.type) {
						case 'person': {
							userType = 'private';
							break;
						}
						case 'organization': {
							userType = 'business';
							break;
						}
					}
					actionField = {
						step: 3,
						user_type: userType,
					};
					break;
				}
				case 4: {
					actionField = {
						step: 4,
						option: 'Összefoglalás',
					};
					break;
				}
			}

			const gtmTag: AnalyticsEventEcommerceCheckoutData = {
				event: 'checkout',
				ecommerce: {
					checkout: {
						actionField,
						products,
						cart_id: order.orderId ?? '?',
					},
				},
				...this.userData,
				...this.languageData,
			};

			if (step >= 2) {
				gtmTag['userData'] = {
					email: order.pickupAddress?.contactEmail,
					phone_number: order.pickupAddress?.contactPhone,
				};
			}

			console.log('push gtm data', gtmTag);

			this.gtmService.pushTag(gtmTag).catch(() => void 0);
		} catch (e) {
			console.warn(e);
		}
	}

	public pushEcommerceEventTransaction(id: string, payment: { transactionId: string; amount: number }): void {
		try {
			const orderData = sessionStorage.getItem('orderData');
			if (typeof orderData !== 'string') return;
			const { order, products } = JSON.parse(orderData) as MappedAnalyticsData;

			const gtmTag: AnalyticsEventEcommerceTransactionData = {
				event: 'transaction',
				userData: {
					email: order.pickupAddress?.contactEmail,
					phone_number: order.pickupAddress?.contactPhone,
				},
				ecommerce: {
					currencyCode: 'HUF',
					purchase: {
						actionField: {
							id,
							transactionId: payment.transactionId,
							revenue: payment.amount,
							affiliation: 'ecsomag',
						},
						products,
						cart_id: order.orderId ?? '?',
					},
				},
				...this.userData,
				...this.languageData,
			};
			console.log('push gtm data', gtmTag);

			this.gtmService.pushTag(gtmTag).catch(() => void 0);
			sessionStorage.removeItem('orderData');
		} catch (e) {
			console.warn(e);
		}
	}

	public pushEcommerceEventTransactionFailed(error_message: string): void {
		try {
			const orderData = sessionStorage.getItem('orderData');
			if (typeof orderData !== 'string') return;
			const { order, products } = JSON.parse(orderData) as MappedAnalyticsData;

			const gtmTag: AnalyticsEventEcommerceTransactionErrorData = {
				event: 'transaction_error',
				userData: {
					email: order.pickupAddress?.contactEmail,
					phone_number: order.pickupAddress?.contactPhone,
				},
				ecommerce: {
					currencyCode: 'HUF',
					checkout: {
						actionField: {
							error_message,
						},
						products,
					},
				},
				...this.userData,
				...this.languageData,
			};
			console.log('push gtm data', gtmTag);

			this.gtmService.pushTag(gtmTag).catch(() => void 0);
			sessionStorage.removeItem('orderData');
		} catch (e) {
			console.warn(e);
		}
	}

	public async pushEcommerceEventUser(event: AnalyticsEventUserType, errors?: string[]): Promise<void> {
		try {
			const gtmTag: AnalyticsUserEventData = {
				event,
				...this.userData,
				...this.languageData,
			};
			if (errors) gtmTag.errors = errors;
			console.log('push gtm data', gtmTag);

			await this.gtmService.pushTag(gtmTag);
		} catch (e) {
			console.warn(e);
		} finally {
			return;
		}
	}
}

export const mapOrderStateToAnalyticsData = (order: OrderStore.State, parcelSummaryTable: ParcelSummaryTableRow[]) => {
	const firstMileType = order.firstMileType;
	const orderData = omit(order, ['ids', 'entities']);
	if (orderData.firstMileType !== 'pick-and-ship') orderData.pickupDate = undefined;
	return {
		order: orderData,
		products: parcelSummaryTable.map((row): AnalyticsEventEcommerceDataProduct => {
			const common: AnalyticsEventEcommerceDataProduct = {
				type: row.type,
				name: row.name,
				quantity: row.amount,
				brand: 'GLS',
			};
			switch (row.type) {
				case 'parcel': {
					const size = row.data?.parcelSize?.size;
					const destinationType = row.data?.destination.type;
					const destinationIsoCode = row.data?.destination.isoCode;
					return {
						id: `${row.type}-${size}-${destinationIsoCode}`,
						...common,
						price: row.price,
						category: firstMileType,
						category2: destinationType,
						category3: destinationIsoCode,
						variant: size,
					};
				}
				case 'extraService': {
					return {
						id: `${row.type}-${row.data.key}`,
						...common,
						price: row.price,
					};
				}
				case 'discount': {
					return {
						id: `${row.type}-${row.data.id}`,
						...common,
						price: row.priceTotal,
					};
				}
				default: {
					return common;
				}
			}
		}),
	};
};
type MappedAnalyticsData = ReturnType<typeof mapOrderStateToAnalyticsData>;
